import React, { useState, useRef, useCallback, useMemo } from 'react';
import {
  Chip, Box, Button,
  Typography, 
  Card, CardContent, CardHeader, TextField, Grid
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useTranslation } from 'react-i18next';
import TagsDropdown from '../TagsDropdown';
import ActionSettingsLayout from '../ActionSettingsHeader';
import { useActionSettings } from '../../hooks/useActionSettings';
import TagEditor from '../Widgets/TagEditor';

const SettingSendTeamsMessage = ({ caseid, tags, settingId, onSaveStatusChange, initialScope = 'event' }) => {
  const { t } = useTranslation();
  
  const [teamId, setTeamId] = useState('');
  const [channelId, setChannelId] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [messageTemplate, setMessageTemplate] = useState('');
  const [anchorEl, setAnchorEl] = useState(null); 
  const [menuType, setMenuType] = useState('');

  const editorRef = useRef(null);
  const teamIdRef = useRef(null);
  const channelIdRef = useRef(null);
  const accessTokenRef = useRef(null);

  // Unified action settings hook
  const {
    isEnabled,
    setIsEnabled,
    conditions,
    isSaveDisabled,
    forceRunLoading,
    forceRunResult,
    popUpOpen,
    forceRunWarningOpen,
    forceRunWarningCount,
    handleSave,
    handleForceRun,
    handleForceRunConfirm,
    handleForceRunCancel,
    handleConditionsChange,
    handleCloseStatusWindow,
    ws
  } = useActionSettings({
    settingId,
    caseid,
    actionType: 'sendteamsmessage',
    onSaveStatusChange,
    initialScope,
    customComparison: (initial) => {
      return teamId === (initial.team_id || '') &&
             channelId === (initial.channel_id || '') &&
             accessToken === (initial.access_token || '') &&
             messageTemplate === (initial.message_template || '');
    },
    customSaveData: () => ({
      team_id: teamId,
      channel_id: channelId,
      access_token: accessToken,
      message_template: messageTemplate
    }),
    loadDataFunction: (data) => {
      if (data.customData?.team_id !== undefined) setTeamId(data.customData.team_id);
      if (data.customData?.channel_id !== undefined) setChannelId(data.customData.channel_id);
      if (data.customData?.access_token !== undefined) setAccessToken(data.customData.access_token);
      if (data.customData?.message_template !== undefined) setMessageTemplate(data.customData.message_template);
    }
  });

  // Process tags for the dropdown/chips
  const availableTags = useMemo(() => {
    if (!tags) return [];
    if (typeof tags === 'string') {
      try {
        const parsed = JSON.parse(tags);
        return Array.isArray(parsed) ? parsed : [parsed];
      } catch (e) {
        return [];
      }
    }
    return Array.isArray(tags) ? tags : [];
  }, [tags]);

  const handleMenuOpen = (event, type) => {
    setAnchorEl(event.currentTarget);
    setMenuType(type);
  };
  const handleMainTagMenuClose = () => setAnchorEl(null);
  const handleTagSelect = (value) => {
    if (menuType === 'teamId') {
      teamIdRef.current?.insertTag(value);
    } else if (menuType === 'channelId') {
      channelIdRef.current?.insertTag(value);
    } else if (menuType === 'accessToken') {
      accessTokenRef.current?.insertTag(value);
    } else if (editorRef.current) {
      editorRef.current.insertTag(value);
    }
    setAnchorEl(null);
  };

  return (
    <ActionSettingsLayout
      title="Send Teams Message"
      isEnabled={isEnabled}
      onEnabledChange={setIsEnabled}
      isSaveDisabled={isSaveDisabled}
      onSave={handleSave}
      onForceRun={handleForceRun}
      forceRunLoading={forceRunLoading}
      forceRunResult={forceRunResult}
      popUpOpen={popUpOpen}
      onCloseStatusWindow={handleCloseStatusWindow}
      forceRunWarningOpen={forceRunWarningOpen}
      forceRunWarningCount={forceRunWarningCount}
      onForceRunConfirm={handleForceRunConfirm}
      onForceRunCancel={handleForceRunCancel}
      conditions={conditions}
      onConditionsChange={handleConditionsChange}
      tags={tags}
    >
      {/* Teams Config */}
      <Card sx={{ 
        mb: 2, 
        borderRadius: '12px', 
        boxShadow: '0 4px 20px rgba(0,0,0,0.04)', 
        border: '1px solid #5757b010', 
        overflow: 'hidden' 
      }}>
        <CardContent sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block' }}>
                  Team ID
                </Typography>
                <TagEditor
                  ref={teamIdRef}
                  value={teamId}
                  onChange={setTeamId}
                  placeholder="Team ID"
                  singleLine
                  onTagClick={(e) => handleMenuOpen(e, 'teamId')}
                  tags={availableTags}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block' }}>
                  Channel ID
                </Typography>
                <TagEditor
                    ref={channelIdRef}
                    value={channelId}
                    onChange={setChannelId}
                    placeholder="Channel ID"
                    singleLine
                    onTagClick={(e) => handleMenuOpen(e, 'channelId')}
                    tags={availableTags}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block' }}>
                  Access Token
                </Typography>
                <TagEditor
                    ref={accessTokenRef}
                    value={accessToken}
                    onChange={setAccessToken}
                    placeholder="Access Token"
                    singleLine
                    onTagClick={(e) => handleMenuOpen(e, 'accessToken')}
                    tags={availableTags}
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Message Template */}
      <Card sx={{ 
        borderRadius: '12px', 
        boxShadow: '0 4px 20px rgba(0,0,0,0.04)', 
        border: '1px solid #5757b010', 
        overflow: 'hidden' 
      }}>
        <CardHeader
          title={<Typography sx={{ fontWeight: 700, color: '#444', fontSize: { xs: '1rem', sm: '1.25rem' } }}>Message Template</Typography>}
          sx={{ borderBottom: '1px solid #f0f0f8', backgroundColor: '#fafbff', p: { xs: 1.5, sm: 2 } }}
        />
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box sx={{
            border: '1px solid #d0d0e8',
            borderRadius: '12px',
            backgroundColor: '#fff',
            p: 0,
            overflow: 'hidden',
            '&:hover': {
              borderColor: '#5757b0',
              boxShadow: '0 4px 12px rgba(87, 87, 176, 0.08)',
            },
            transition: 'all 0.2s ease',
          }}>
            <TagEditor
              ref={editorRef}
              value={messageTemplate}
              onChange={setMessageTemplate}
              placeholder="Write Teams message template. You can use tags like {filename}..."
              onTagClick={handleMenuOpen}
              tags={availableTags}
            />
            
            <Box sx={{ 
              p: 2, 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 1,
              backgroundColor: '#f8f9ff',
              borderTop: '1px solid #eaeaf0',
            }}>
              <Typography sx={{ fontWeight: 600, color: '#5757b0', fontSize: '0.85rem', mr: 1 }}>Quick Tags:</Typography>
              {availableTags && availableTags.length > 0 ? (
                availableTags.slice(0, 6).map(tag => (
                  <Chip
                    key={tag.variable}
                    label={tag.variable}
                    onClick={() => {
                        // Ensure we prevent default to avoid focus issues
                        if (editorRef.current) {
                            editorRef.current.focus();
                            // Small delay to ensure focus is applied before insertion
                            setTimeout(() => {
                                editorRef.current.insertTag(tag.variable);
                            }, 10);
                        }
                    }}
                    size="small"
                    sx={{
                      backgroundColor: '#f0f0ff',
                      color: '#5757b0',
                      borderColor: '#5757b030',
                      border: '1px solid',
                      fontWeight: 600,
                      transition: 'all 0.2s ease',
                    }}
                  />
                ))
              ) : (
                <Typography variant="caption" sx={{ color: '#999', fontStyle: 'italic' }}>
                  No tags available
                </Typography>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
      
      <TagsDropdown
        tags={availableTags}
        handleTagSelect={handleTagSelect}
        anchorEl={anchorEl}
        setAnchorEl={handleMainTagMenuClose}
        menuType={menuType}
      />
    </ActionSettingsLayout>
  );
};

export default React.memo(SettingSendTeamsMessage);
